import React from "react";
import { Link } from "react-router-dom";
import { m, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import {
  ArrowRight,
  CalendarRange,
  CheckCircle2,
  CreditCard,
  FileSpreadsheet,
  IndianRupee,
  ReceiptText,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";
import { Badge } from "../../components/ui/primitives";
import { cn } from "../../lib/utils";
import { BrandCopy } from "../../components/BrandWordmarkText";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";

/* Keep these fallbacks byte-for-byte aligned with backend/app/core/marketing.py
   DEFAULTS["why_switch"]. The visual treatment is product-managed; the copy remains CMS-managed.
   Exported because POS-11 renders a CONDENSED 3-chip strip of this same band above the module
   catalogue on the homepage — the chips read from this one list so the two can never fork. */
export const DEFAULT_CARDS = [
  {
    id: "w1",
    title: "Invoice-bound payment evidence",
    body: "Create collection orders in the school's connected Razorpay or Cashfree account and mark an invoice paid only after verified provider evidence.",
    footnote: "Final bank credit, timing and deductions remain provider or bank evidence for the school to reconcile.",
  },
  {
    id: "w2",
    title: "90 days free — a full term",
    body: "Ninety days is a whole term. Collect real fees, publish real results — then decide. No card.",
    footnote: "Public fixed billing and the reviewed per-student proposal model are shown separately.",
  },
  {
    id: "w3",
    title: "Move your student data with a dry run first",
    body: "For student CSV/XLSX migration, the copilot suggests a column map and your team validates every row in a dry run before committing the import.",
    footnote: "The current committed path imports student records with quiet-mode controls and rollback.",
  },
];

const EVIDENCE_ACTIONS = [
  { href: "/modules/online-payment", label: "Review fee collection" },
  { href: "/pricing", label: "Review plan terms" },
  { href: "/switch", label: "See how migration works" },
];

const STAGE_META = [
  { label: "Getting fees in", proof: "Evidence before status" },
  { label: "Trying it out", proof: "A term before a decision" },
  { label: "Moving your data in", proof: "Review before commit" },
];

/* Scroll-scrub pin guard (2026-08-20) — mirrors HeroScrollReveal's decided thresholds: the pinned
   horizontal journey runs only on a roomy desktop viewport with motion ON. Everything else — small
   screens, prefers-reduced-motion, the footer Pause switch, and every e2e run (Playwright's default
   1280x720 fails the height gate) — renders the untouched editorial list below, so the section's
   ids, testids and copy are identical in both worlds. */
const PIN_MIN_W = 1280;
const PIN_MIN_H = 800;

function usePinnableViewport() {
  const [fits, setFits] = React.useState(false);
  React.useEffect(() => {
    const measure = () => setFits(window.innerWidth >= PIN_MIN_W && window.innerHeight >= PIN_MIN_H);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  return fits;
}

function MiniArrow() {
  return <ArrowRight className="h-3.5 w-3.5 shrink-0 text-ink-faint" strokeWidth={2} aria-hidden="true" />;
}

function PaymentEvidenceVisual() {
  const nodes = [
    [IndianRupee, "Own gateway"],
    [ShieldCheck, "Provider evidence"],
    [ReceiptText, "Invoice state"],
  ];

  return (
    <div className="relative overflow-hidden border border-line bg-surface p-4 sm:p-5" aria-hidden="true">
      <div className="absolute inset-x-0 top-0 h-0.5 bg-primary" />
      <p className="mb-5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-subtle">Evidence path</p>
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5">
        {nodes.map(([Icon, label], index) => (
          <React.Fragment key={label}>
            <span className="min-w-0">
              <span className={cn(
                "grid h-10 w-10 place-items-center border",
                index === 1
                  ? "border-positive/25 bg-positive/10 text-positive"
                  : "border-primary/20 bg-primary/[0.07] text-primary",
              )}>
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.9} />
              </span>
              <span className="mt-2 block text-[10px] font-semibold leading-tight text-ink-2 sm:text-[10.5px]">{label}</span>
            </span>
            {index < nodes.length - 1 && <MiniArrow />}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-5 border-t border-dashed border-line pt-3 text-[10px] font-medium leading-relaxed text-ink-subtle">
        Payment status follows the verified record — not a screen update alone.
      </div>
    </div>
  );
}

function TrialVisual() {
  return (
    <div className="relative overflow-hidden border border-primary/20 bg-primary/[0.055] p-4 sm:p-5" aria-hidden="true">
      <div className="absolute -right-14 -top-14 h-36 w-36 rounded-full bg-primary/[0.10] blur-3xl" />
      <p className="relative text-[10px] font-bold uppercase tracking-[0.18em] text-ink-subtle">Evaluation window</p>
      <div className="relative mt-5 flex items-end justify-between gap-4">
        <span className="flex items-end gap-2">
          <span className="font-heading text-[64px] font-extrabold leading-[0.76] tracking-[-0.075em] text-primary tabular-nums sm:text-[72px]">90</span>
          <span className="pb-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-ink-subtle">days</span>
        </span>
        <span className="inline-flex items-center gap-1.5 border border-line bg-surface px-2.5 py-1.5 text-[10px] font-semibold text-ink-2">
          <CreditCard className="h-3.5 w-3.5 text-primary" strokeWidth={1.9} /> No card
        </span>
      </div>
      <div className="relative mt-6 grid grid-cols-[auto_1fr_auto_1fr_auto] items-center gap-2 text-[10px] font-semibold text-ink-2">
        <span>Set up</span><span className="h-px bg-primary/25" /><span>Run the term</span><span className="h-px bg-primary/25" /><span>Decide</span>
      </div>
    </div>
  );
}

function MigrationVisual() {
  const steps = [
    [UploadCloud, "Upload"],
    [FileSpreadsheet, "Map"],
    [CheckCircle2, "Validate"],
    [ShieldCheck, "Commit"],
  ];

  return (
    <div className="relative overflow-hidden border border-orange-border bg-orange-soft/55 p-4 sm:p-5" aria-hidden="true">
      <div className="absolute inset-x-0 top-0 h-0.5 bg-orange" />
      <div className="mb-5 flex items-center justify-between gap-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-dark">Reviewed import</p>
        <span className="border border-orange-border bg-surface/80 px-2 py-1 text-[9.5px] font-semibold text-ink-2">Student CSV/XLSX</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {steps.map(([Icon, label], index) => (
          <span key={label} className="relative min-w-0">
            {index > 0 && <span className="absolute right-1/2 top-5 h-px w-full bg-orange-border" />}
            <span className="relative z-10 grid h-10 w-10 place-items-center border border-orange-border bg-surface text-orange-dark shadow-sm">
              <Icon className="h-[17px] w-[17px]" strokeWidth={1.9} />
            </span>
            <span className="mt-2 block text-[10px] font-semibold leading-tight text-ink-2 sm:text-[10.5px]">{label}</span>
          </span>
        ))}
      </div>
      <div className="mt-5 border-t border-dashed border-orange-border pt-3 text-[10px] font-medium leading-relaxed text-ink-subtle">
        The dry run is where your team checks the suggested column mapping.
      </div>
    </div>
  );
}

function NeutralEvidenceVisual() {
  return (
    <div className="border border-line bg-muted/55 p-5" aria-hidden="true">
      <span className="grid h-11 w-11 place-items-center border border-primary/20 bg-primary/[0.07] text-primary">
        <ShieldCheck className="h-5 w-5" strokeWidth={1.9} />
      </span>
      <span className="mt-5 block text-[10px] font-bold uppercase tracking-[0.18em] text-ink-subtle">Documented scope</span>
      <span className="mt-1 block text-sm font-semibold text-ink">Review the evidence before rollout.</span>
    </div>
  );
}

function EvidenceVisual({ index }) {
  if (index === 0) return <PaymentEvidenceVisual />;
  if (index === 1) return <TrialVisual />;
  if (index === 2) return <MigrationVisual />;
  return <NeutralEvidenceVisual />;
}

/* The ONE evidence-row renderer — both the editorial list and the pinned journey render this exact
   article, so the two presentations can never fork in copy or affordances. */
function EvidenceArticle({ card, index }) {
  const action = EVIDENCE_ACTIONS[index] || { href: "/modules", label: "Review documented scope" };
  const meta = STAGE_META[index] || { label: "Going live", proof: "Document the next action" };

  return (
    <article className="group/evidence grid gap-6 py-8 first:pt-0 sm:gap-7 sm:py-10 lg:grid-cols-[5.25rem_minmax(0,1fr)_minmax(14rem,0.78fr)] lg:items-center lg:gap-9">
      <div className="flex items-center gap-3 lg:block lg:self-stretch">
        <span className="font-heading text-[11px] font-bold tracking-[0.18em] text-ink-subtle tabular-nums">{String(index + 1).padStart(2, "0")}</span>
        <span className="h-px w-9 bg-line lg:mt-4 lg:block lg:w-11" aria-hidden="true" />
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary lg:mt-4 lg:block">{meta.label}</span>
      </div>

      <div className="min-w-0">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-ink-subtle">{meta.proof}</p>
        <h3 className="text-balance font-heading text-[22px] font-bold leading-tight tracking-tight text-ink sm:text-[25px]">
          <BrandCopy>{card.title}</BrandCopy>
        </h3>
        {card.body && <p className="mt-3 max-w-2xl text-pretty text-[14px] leading-[1.75] text-ink-2 sm:text-[15px]"><BrandCopy>{card.body}</BrandCopy></p>}
        {card.footnote && (
          <p className="mt-5 flex max-w-2xl items-start gap-2.5 border-l border-positive/40 pl-3 text-[12px] leading-relaxed text-ink-subtle sm:text-[12.5px]">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-positive" strokeWidth={1.9} aria-hidden="true" />
            <span><BrandCopy>{card.footnote}</BrandCopy></span>
          </p>
        )}
        <Link to={action.href} className="mt-5 inline-flex min-h-11 items-center gap-2 text-[13px] font-semibold text-primary outline-none transition-colors duration-fast hover:text-accent-hover focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas">
          {action.label}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>

      <EvidenceVisual index={index} />
    </article>
  );
}

function SectionBackdrop() {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0 opacity-45"
        style={{
          backgroundImage: "linear-gradient(rgb(var(--vp-border-default) / 0.34) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--vp-border-default) / 0.34) 1px, transparent 1px)",
          backgroundSize: "54px 54px",
          maskImage: "linear-gradient(to bottom, black, transparent 76%)",
          WebkitMaskImage: "linear-gradient(to bottom, black, transparent 76%)",
        }}
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-primary/[0.08] blur-3xl" aria-hidden="true" />
    </>
  );
}

function SectionHeader({ c, compact = false }) {
  return (
    <header className={cn(
      "grid gap-8 border-b border-line lg:grid-cols-12 lg:items-end lg:gap-10",
      compact ? "pb-6" : "pb-10 sm:pb-12",
    )}>
      <div className="lg:col-span-7">
        <Badge tone="blue" className="mb-4"><BrandCopy>{c.badge || "Before you compare plans"}</BrandCopy></Badge>
        <h2 id="why-switch-heading" className={cn(
          "max-w-[760px] text-balance font-heading font-extrabold leading-[1.04] tracking-[-0.045em] text-ink",
          compact ? "text-[30px] sm:text-[36px] lg:text-[40px]" : "text-[34px] sm:text-[44px] lg:text-[52px]",
        )}>
          <BrandCopy>{c.heading || "Why schools switch to Vidyapeeth360"}</BrandCopy>
        </h2>
      </div>
      <div className="lg:col-span-4 lg:col-start-9 lg:pb-1">
        <span className="mb-4 block h-px w-16 bg-primary" aria-hidden="true" />
        <p className="text-pretty text-base leading-relaxed text-ink-2 sm:text-lg">
          <BrandCopy>{c.subhead || "Three straight answers to the questions schools ask us before moving."}</BrandCopy>
        </p>
      </div>
    </header>
  );
}

function BriefCopy() {
  return (
    <>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">A decision brief</p>
      <p className="mt-2 max-w-[15rem] font-heading text-lg font-bold leading-snug tracking-tight text-ink">See who does what at every step.</p>
      <p className="mt-3 max-w-[16rem] text-[12px] leading-relaxed text-ink-subtle">Payments, trials and data imports each need a recorded checkpoint — not a promise.</p>
    </>
  );
}

function FooterStrip() {
  return (
    <div className="mt-8 grid gap-3 border-t border-line pt-5 text-[11px] font-semibold text-ink-subtle sm:grid-cols-2 sm:gap-5 sm:text-xs">
      <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-positive" strokeWidth={1.9} aria-hidden="true" /> Scope and limits stay visible before rollout.</span>
      <span className="inline-flex items-center gap-2 sm:justify-self-end"><CalendarRange className="h-4 w-4 text-primary" strokeWidth={1.9} aria-hidden="true" /> Review one real school term, not a teaser.</span>
    </div>
  );
}

/* The editorial evidence list — the section's canonical form, unchanged from before the journey
   scrub existed. The one addition is scroll-LINKED: the decision-brief rail fills top-to-bottom as
   the reader moves through the list (a reading-progress cue tied to the list itself). Under
   reduced motion / pause the rail renders as the original solid primary border — the frozen
   visual-gate state is byte-identical to the pre-scrub section. */
function EditorialEvidenceList({ c, cards }) {
  const reduce = useReducedMotion();
  const { paused } = useMarketingMotion();
  const listRef = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: listRef, offset: ["start 0.78", "end 0.62"] });
  const fill = useSpring(scrollYProgress, { stiffness: 90, damping: 26, restDelta: 0.001 });
  const still = reduce || paused;

  return (
    <section
      id="why-switch"
      className="relative scroll-mt-24 overflow-hidden border-b border-line campus-glass-surface"
      aria-labelledby="why-switch-heading"
    >
      <SectionBackdrop />

      <div className="relative mx-auto max-w-7xl px-5 py-16 sm:py-20 lg:py-28">
        <SectionHeader c={c} />

        <div className="mt-7 grid gap-5 sm:mt-8 lg:grid-cols-[minmax(12rem,0.55fr)_minmax(0,1fr)] lg:gap-12">
          <aside className="relative pl-4 lg:sticky lg:top-28 lg:self-start lg:pl-5" aria-label="A decision brief">
            <span className="absolute inset-y-0 left-0 w-[2px] bg-primary/20" aria-hidden="true" />
            <m.span
              className="absolute inset-y-0 left-0 w-[2px] origin-top bg-primary"
              style={still ? undefined : { scaleY: fill }}
              aria-hidden="true"
            />
            <BriefCopy />
          </aside>

          <ol ref={listRef} className="relative" data-testid="why-switch-cards">
            {cards.map((card, index) => (
              <li key={card.id || card.title} className="relative border-t border-line first:border-t-0">
                <EvidenceArticle card={card} index={index} />
              </li>
            ))}
          </ol>
        </div>

        <FooterStrip />
      </div>
    </section>
  );
}

/* The pinned horizontal journey (2026-08-20) — desktop-only scroll choreography over the SAME
   evidence articles. The three rows are a staged journey by construction (STAGE_META: fees in →
   trying it out → data in), so the horizontal scrub encodes the sequence rather than decorating
   it: the section pins for ~90vh per stage while vertical scroll pans one full-width panel per
   stage, a spring-smoothed track indicator marks where you are, and the decision-brief rail fills
   with the same progress. Keyboard parity: focusing anything inside an off-screen panel jumps the
   window scroll to that panel's position, so focus is never parked out of view. */
function PinnedJourney({ c, cards }) {
  const wrapRef = React.useRef(null);
  const count = cards.length;
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end end"] });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 24, mass: 0.4, restDelta: 0.001 });
  const x = useTransform(progress, [0, 1], ["0%", `-${(((count - 1) / count) * 100).toFixed(4)}%`]);
  const indicatorX = useTransform(progress, [0, 1], ["0%", `${(count - 1) * 100}%`]);

  const focusPanel = (index) => () => {
    const el = wrapRef.current;
    if (!el || count < 2) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const span = Math.max(el.offsetHeight - window.innerHeight, 0);
    window.scrollTo({ top: top + (span * index) / (count - 1), behavior: "auto" });
  };

  return (
    <section
      id="why-switch"
      ref={wrapRef}
      className="relative scroll-mt-24 border-b border-line bg-canvas"
      aria-labelledby="why-switch-heading"
      style={{ height: `${100 + (count - 1) * 85}vh` }}
    >
      <div className="sticky top-0 flex h-screen flex-col overflow-hidden">
        <SectionBackdrop />

        <div className="relative mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col px-5 pb-8 pt-14">
          <SectionHeader c={c} compact />

          <div className="mt-6 grid min-h-0 flex-1 gap-5 lg:grid-cols-[minmax(12rem,0.55fr)_minmax(0,1fr)] lg:gap-12">
            <aside className="relative self-start pl-5" aria-label="A decision brief">
              <span className="absolute inset-y-0 left-0 w-[2px] bg-primary/20" aria-hidden="true" />
              <m.span
                className="absolute inset-y-0 left-0 w-[2px] origin-top bg-primary"
                style={{ scaleY: progress }}
                aria-hidden="true"
              />
              <BriefCopy />

              {/* Track indicator: a fixed rail of one notch per stage; the filled notch slides with
                  the scrub. Decorative — the stage labels inside each panel carry the meaning. */}
              <div className="mt-8 h-1 w-40 overflow-hidden rounded-full bg-line" aria-hidden="true">
                <m.span
                  className="block h-full rounded-full bg-primary"
                  style={{ width: `${100 / count}%`, x: indicatorX }}
                />
              </div>
            </aside>

            <div className="min-h-0 min-w-0 overflow-hidden">
              <m.ol
                className="flex h-full"
                style={{ x, width: `${count * 100}%` }}
                data-testid="why-switch-cards"
              >
                {cards.map((card, index) => (
                  <li
                    key={card.id || card.title}
                    className="flex h-full min-w-0 items-center pr-12"
                    style={{ width: `${100 / count}%` }}
                    onFocus={focusPanel(index)}
                  >
                    <EvidenceArticle card={card} index={index} />
                  </li>
                ))}
              </m.ol>
            </div>
          </div>

          <FooterStrip />
        </div>
      </div>
    </section>
  );
}

export default function WhySwitchSection({ content }) {
  const c = content || {};
  const cards = (Array.isArray(c.cards) && c.cards.length ? c.cards : DEFAULT_CARDS).slice(0, 6);
  const reduce = useReducedMotion();
  const { paused } = useMarketingMotion();
  const fits = usePinnableViewport();
  const pinned = fits && !reduce && !paused && cards.length > 1;

  return pinned ? <PinnedJourney c={c} cards={cards} /> : <EditorialEvidenceList c={c} cards={cards} />;
}
